import React, { useState, useEffect } from 'react';
import { Box, Card, FormControlLabel, Switch, Table, TableBody, TableContainer, TablePagination } from '@mui/material';
import { useSnackbar } from 'notistack';
import Scrollbar from '../../../../../components/Scrollbar';
import { TableEmptyRows, TableHeadCustom, TableNoData, TableSkeleton } from '../../../../../components/table';
import useTable, { emptyRows } from '../../../../../hooks/useTable';
import MerchantTransactionTableRow from './merchantTransactionTableRow';
import { adminApi } from '../../../../../utils/axios';
import { ADMIN_RETAIL_REPORT } from '../../../../../utils/apiUrl';
import MerchantTransactionHeaders from './merchantTransactionHeaders';
import MerchantTransactionsFilter from './merchantTransactionsFilter';
import { fCustomDate } from '../../../../../utils/formatTime';

const TABLE_HEAD = [
  { id: 'date', label: 'Date', align: 'left' },
  { id: 'tran_id', label: 'Tran ID', align: 'left' },
  { id: 'payment_gateway', label: 'Payment Gateway', align: 'left' },
  { id: 'from', label: 'From', align: 'left' },
  { id: 'to', label: 'To', align: 'left' },
  { id: 'transaction_type', label: 'Transaction Type', align: 'left' },
  { id: 'transaction_value', label: 'Transaction Value', align: 'left' },
  { id: 'merchant_receipt', label: 'Merchant Receipt', align: 'left' },
  { id: 'cashback', label: 'Cashback', align: 'left' },
  { id: 'transaction_fee', label: 'Transaction Fee', align: 'left' },
  { id: 'stb_transaction_fee', label: 'STB Transaction Fee', align: 'left' },
  { id: 'stb_earning', label: 'STB Earning', align: 'left' },
  { id: 'stb_loss', label: 'STB Loss', align: 'left' },
  { id: 'status', label: 'Status', align: 'left' },
];

const MerchantTransactionsData = () => {
  // Getting Current Date
  const currentDate = new Date();

  // Getting Date which is 30 Days back from current date
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(currentDate.getDate() - 30);

  const { dense, page, order, orderBy, rowsPerPage, setPage, onChangeDense, onChangePage, onChangeRowsPerPage } =
    useTable();

  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState({ count: 0, data: [] });
  const [filterParameters, setFilterParameters] = useState({
    search: '',
    transaction_type: '',
    status: 'processed',
    from_date: fCustomDate(new Date(thirtyDaysAgo)),
    to_date: fCustomDate(new Date(currentDate)),
  });
  const [isProfitLoss, setIsProfitLoss] = useState(0);
  const [totalData, setTotalData] = useState(null);
  const [netEarning, setNetEarning] = useState(0);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    getMerchantTransaction();
  }, [rowsPerPage, page, filterParameters]);

  useEffect(() => {
    if (totalData) {
      const totalEarning = totalData.stb_fee - totalData.stb_expense;
      setIsProfitLoss(Math.sign(totalEarning));
      setNetEarning(totalEarning);
    }
  }, [totalData, setIsProfitLoss, setNetEarning]);

  // Getting Merchant Transaction Data
  const getMerchantTransaction = async () => {
    // console.log('filter parameters', filterParameters);
    setIsLoading(true);
    try {
      const response = await adminApi.get(ADMIN_RETAIL_REPORT, {
        params: {
          is_merchant: true,
          offset: rowsPerPage * page,
          limit: rowsPerPage,
          ...filterParameters,
        },
      });
      console.log(response, 'response');
      setIsLoading(false);
      setTableData(response?.data);
      setTotalData({ ...response?.data.totals, total_transactions: response?.data.count });
    } catch (error) {
      setIsLoading(false);
      enqueueSnackbar(error?.message, { variant: 'error' });
      setTableData(error);
    }
  };

  // If there is no data
  const isNotFound = tableData?.results?.length === 0;

  // Check Dense Height
  const denseHeight = dense ? 52 : 72;

  return (
    <Card sx={{ margin: '40px 0px' }}>
      {/* Merchant Report Filter */}
      <MerchantTransactionsFilter
        filterParameters={filterParameters}
        onFilterChange={(val) => {
          setFilterParameters(val);
          setPage(0);
        }}
        data={tableData?.results}
      />

      {/* Merchant Report Headers */}
      {totalData && (
        <MerchantTransactionHeaders netEarning={netEarning} totalData={totalData} isProfitLoss={isProfitLoss} />
      )}

      {/* Merchant Report Data Table */}
      <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
        <Scrollbar>
          <Table size={dense ? 'small' : 'medium'}>
            <TableHeadCustom order={order} orderBy={orderBy} headLabel={TABLE_HEAD} rowCount={tableData.count} />
            {isLoading ? (
              <>
                <TableSkeleton />
                <TableSkeleton />
                <TableSkeleton />
                <TableSkeleton />
                <TableSkeleton />
              </>
            ) : (
              <TableBody>
                {tableData?.results?.map((row) => (
                  <MerchantTransactionTableRow key={row.id} row={row} />
                ))}
                <TableEmptyRows height={denseHeight} emptyRows={emptyRows(page, rowsPerPage, tableData.count)} />
                <TableNoData isNotFound={isNotFound} />
              </TableBody>
            )}
          </Table>
        </Scrollbar>
      </TableContainer>
      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={tableData.count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />

        <FormControlLabel
          control={<Switch checked={dense} onChange={onChangeDense} />}
          label="Dense"
          sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
        />
      </Box>
    </Card>
  );
};

export default MerchantTransactionsData;
